import { Link } from 'react-router-dom';
import type { ConversationSummary } from '../domain/conversations';
import { useInterval } from '../hooks/useInterval';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { EmptyState, LoadingState, StateForError } from '../components/PageStates';
import UserAvatar from '../components/UserAvatar';
import { conversationTitle } from '../utils/conversations';
import { Button } from '@/components/ui/button';
import { MessageCircle, Package, PenSquare, Users } from 'lucide-react';

/** How often the list looks for new messages (main's interval). */
export const INBOX_REFRESH_MS = 10_000;

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * `/conversations` — my conversations, newest activity first, with what I
 * may read of each (018 FR-012, FR-017). Refreshes quietly while open.
 */
export default function InboxPage() {
  const { conversations } = useRepositories();
  const list = useLoad(() => conversations.list(), 'conversations');
  useInterval(list.refresh, INBOX_REFRESH_MS, list.status === 'ready');

  const newMessage = (
    <Button className="rounded-full" asChild>
      <Link to="/conversations/new">
        <PenSquare />
        New message
      </Link>
    </Button>
  );

  const isEmpty = list.status === 'ready' && list.data.length === 0;
  let body;
  if (list.status === 'loading') body = <LoadingState label="Loading messages" />;
  else if (list.status === 'error')
    body = <StateForError error={list.error} onRetry={list.reload} what="Messages" />;
  else if (isEmpty)
    body = (
      <EmptyState
        icon={<MessageCircle className="size-8" />}
        title="No conversations yet"
        description="Message someone from their listing, or start a new message."
        action={newMessage}
      />
    );
  else
    body = (
      <ul aria-label="Conversations" className="space-y-2">
        {list.data.map((c) => (
          <li key={c.id}>
            <ConversationRow conversation={c} />
          </li>
        ))}
      </ul>
    );

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Messages</h1>
        {/* The empty state offers it instead. */}
        {!isEmpty && newMessage}
      </div>
      {body}
    </div>
  );
}

function ConversationRow({ conversation: c }: { conversation: ConversationSummary }) {
  const first = c.otherPeople[0];
  return (
    <Link
      to={`/conversations/${c.id}`}
      className="flex items-center gap-4 rounded-3xl bg-card p-4 transition-colors hover:bg-secondary"
    >
      {c.kind === 'group' || !first ? (
        <div
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-secondary"
          aria-hidden="true"
        >
          <Users className="size-5 text-secondary-foreground" />
        </div>
      ) : (
        <UserAvatar person={first} className="size-11" />
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p
            className={`min-w-0 flex-1 truncate text-foreground ${c.hasUnread ? 'font-bold' : 'font-semibold'}`}
          >
            {conversationTitle(c)}
          </p>
          {c.lastMessageAt && (
            <time
              dateTime={c.lastMessageAt}
              className={`shrink-0 text-xs ${c.hasUnread ? 'font-semibold text-tertiary' : 'text-muted-foreground'}`}
            >
              {formatWhen(c.lastMessageAt)}
            </time>
          )}
        </div>
        {c.listing && (
          <p className="flex items-center gap-1 truncate text-xs font-medium text-wood">
            <Package className="size-3 shrink-0" aria-hidden="true" />
            {c.listing.title}
          </p>
        )}
        {c.lastMessagePreview && (
          <p
            className={`truncate text-sm ${c.hasUnread ? 'text-foreground' : 'text-muted-foreground'}`}
          >
            {c.lastMessagePreview}
          </p>
        )}
      </div>
      {c.hasUnread && (
        <span
          role="img"
          aria-label="unread"
          className="size-2.5 shrink-0 rounded-full bg-tertiary"
        />
      )}
    </Link>
  );
}
